import { useState } from "react";
import { FieldArray, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import ActionMenu from "../../../components/actionMenu/ActionMenu";
import ContentCard from "../../../components/misc/ContentCard";
import FormButtons from "../../../components/misc/FormButtons";
import TextInput from "../../../components/misc/TextInput";
import TableWidget from "../../../components/tables/TableWidget";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../helpers/apiHelper";
import { postCreateRecallEntry } from "../../../redux/api/recall.api";
import { useAppDispatch } from "../../../redux/hooks";
import { RecallEntrySummaryInterface } from "../../../redux/interfaces/recall.interface";
import { recallRoutes } from "../../../router/routesConstants";
import Text from "../../../styleguide/Text";
import { dateForDatabase } from "../../../utils/dateAndTime";
import RecallProductForm, {
  RecallProductFormValues,
  recallProductSchema,
} from "./components/RecallProductForm";
import {
  getRecallProductsTableData,
  recallProductsTableHeader,
} from "./components/recallProductsTableConfig";

interface RecallFormValues {
  documentNumber: string;
  lines: RecallProductFormValues[];
}

const initialValues: RecallFormValues = {
  documentNumber: "",
  lines: [],
};

const recallSchema = Yup.object({
  documentNumber: Yup.string()
    .trim()
    .max(100, "Numarul documentului poate avea maximum 100 de caractere")
    .required("Campul este obligatoriu"),
  lines: Yup.array()
    .of(recallProductSchema)
    .min(1, "Adauga cel putin un produs"),
});

function CreateRecallEntry() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasPendingProduct, setHasPendingProduct] = useState(false);

  const handleSave = async (values: RecallFormValues) => {
    const resultAction = await dispatch(
      postCreateRecallEntry({
        documentNumber: values.documentNumber.trim(),
        lines: values.lines.map((line) => ({
          productCode: line.productCode.trim(),
          lot: line.lot.trim(),
          bbd: dateForDatabase(line.bbd),
        })),
      }),
    );

    if (
      checkEndpointStatus(resultAction, postCreateRecallEntry) ===
      endpointStatus.fulfilled
    ) {
      const { idCampaign } =
        resultAction.payload as RecallEntrySummaryInterface;
      navigate(`/${recallRoutes.recall}/${idCampaign}`);
    }
  };

  return (
    <ContentCard cardTitle="Creeaza retragere">
      <Formik
        initialValues={initialValues}
        validationSchema={recallSchema}
        validateOnMount
        onSubmit={handleSave}>
        {({
          values,
          errors,
          touched,
          isValid,
          isSubmitting,
          getFieldProps,
          submitForm,
        }) => (
          <div>
            <TextInput
              label="Numar document"
              name="documentNumber"
              inputProps={getFieldProps("documentNumber")}
              error={errors.documentNumber}
              touched={touched.documentNumber}
              disabled={isSubmitting}
            />
            <Text
              variant="h4"
              component="h4"
              className="font-semibold"
              gutterBottom>
              Produse pentru retragere
            </Text>
            <FieldArray name="lines">
              {({ push, remove }) => (
                <>
                  <RecallProductForm
                    disabled={isSubmitting}
                    onAdd={push}
                    onPendingChange={setHasPendingProduct}
                  />
                  {values.lines.length > 0 && (
                    <TableWidget
                      className="[&_thead_th]:text-left"
                      tableTitle="Produse adaugate"
                      tableHeaderData={recallProductsTableHeader}
                      tableItemsData={getRecallProductsTableData(values.lines)}
                      withTablePagination={false}
                      ActionButtonsComponent={({
                        itemId,
                      }: {
                        itemId: string | number;
                      }) => (
                        <ActionMenu
                          minItemsForMenu={2}
                          ariaLabel={`Actiuni pentru produsul ${
                            Number(itemId) + 1
                          }`}
                          items={[
                            {
                              id: "remove-product",
                              label: "Elimina produs",
                              icon: "DELETE",
                              color: "error",
                              tooltip: "Elimina produsul din retragere",
                              disabled: isSubmitting,
                              onClick: () => remove(Number(itemId)),
                            },
                          ]}
                        />
                      )}
                    />
                  )}
                </>
              )}
            </FieldArray>
            <FormButtons
              goBackLabel="Anuleaza"
              goNextLabel="Creeaza retragere"
              nextButtonIcon="ADD"
              handleGoBack={() => navigate(`/${recallRoutes.recall}`)}
              nextButtonLoading={isSubmitting}
              nextButtonType="button"
              handleGoNext={() => submitForm()}
              nextButtonDisabled={!isValid || isSubmitting || hasPendingProduct}
            />
          </div>
        )}
      </Formik>
    </ContentCard>
  );
}

export default CreateRecallEntry;
